import React, { useEffect, useRef, useState } from "react"
import { AppBootSkeleton } from "components/new-stuff/layout/AppBootSkeleton"
import { markAccountSwitch } from "bootMode"
import { useLocation, useNavigate } from "react-router-dom"
import { isLocalOrDevOrStaging, isPadeDomain } from "utils/utils"
import {
  ClockIcon,
  GiftIcon,
  MagnifyingGlassIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/24/outline"

import {
  Squares2X2Icon,
  PlusIcon,
  ChevronDownIcon,
} from "@heroicons/react/24/solid"
import { useAppNotificationStore, useAuthStore } from "stores"
import { useMutation } from "@tanstack/react-query"
import { newArchAxiosInstance } from "utils/request"
import { Tooltip } from "antd"
import { IUser } from "stores/auth"
import { useCustomizationSettingsStore } from "stores/customization-settings"
import PadeLogoWhite from "assets/logos/paidhr-logo-white.svg"

// Side navigation glyphs from the design system. Authored with `currentColor`
// so each one takes its tint from the row (see NavTab's iconTint).
import { ReactComponent as DashboardIcon } from "assets/svg/side-nav/dashboard.svg"
import { ReactComponent as CompanyIcon } from "assets/svg/side-nav/company.svg"
import { ReactComponent as EmployeeIcon } from "assets/svg/side-nav/employee.svg"
import { ReactComponent as HiringIcon } from "assets/svg/side-nav/hiring.svg"
import { ReactComponent as RequestIcon } from "assets/svg/side-nav/request.svg"
import { ReactComponent as SurveyIcon } from "assets/svg/side-nav/survey.svg"
import { ReactComponent as DocumentsIcon } from "assets/svg/side-nav/documents.svg"
import { ReactComponent as PayrollIcon } from "assets/svg/side-nav/payroll.svg"
import { ReactComponent as PayScheduleIcon } from "assets/svg/side-nav/pay-schedule.svg"
import { ReactComponent as WalletIcon } from "assets/svg/side-nav/wallet.svg"
import { ReactComponent as EwaIcon } from "assets/svg/side-nav/earned-wage-access.svg"
import { ReactComponent as PerformanceIcon } from "assets/svg/side-nav/performance.svg"
import { ReactComponent as TimeAndAttendanceIcon } from "assets/svg/side-nav/time-and-attendance.svg"
import { ReactComponent as TaskIcon } from "assets/svg/side-nav/task.svg"
import { ReactComponent as DisciplinaryIcon } from "assets/svg/side-nav/disciplinary.svg"
import { ReactComponent as SettingsIcon } from "assets/svg/side-nav/settings.svg"
import { ReactComponent as ReportsIcon } from "assets/svg/side-nav/reports.svg"
import { ReactComponent as AssetsIcon } from "assets/svg/side-nav/assets.svg"
import { ReactComponent as AuditLogIcon } from "assets/svg/side-nav/audit-log.svg"
import { ReactComponent as AnnouncementsIcon } from "assets/svg/side-nav/announcements.svg"
import { ReactComponent as CalendarsIcon } from "assets/svg/side-nav/calendars.svg"
import { devUrl, localUrl, stagingUrl } from "utils/constants"
import usePermissions from "hooks/usePermissions"
import { trackEvent } from "utils/analytics"
import {
  ActionType,
  BrandMark,
  NavTab,
  SectionHeading,
  SideNavFrame,
  fadeInWhenCompact,
  fadeOutWhenCompact,
  useSideNavCollapse,
} from "./side-nav"

export const SideNavigationBar: React.FC<{
  isSidebarOpen: boolean
  toggleSidebar: React.Dispatch<React.SetStateAction<boolean>>
}> = ({ isSidebarOpen, toggleSidebar }) => {
  const [isBusinessListOpen, toggleBusinessListOpen] = useState<boolean>(false)
  const [businessSearch, setBusinessSearch] = useState("")
  const businessSearchRef = useRef<HTMLInputElement>(null)
  const { customizationSettings } = useCustomizationSettingsStore()

  const { compact, setCollapsed } = useSideNavCollapse()

  // module_tab_switched.
  //
  // Driven by the route rather than by a click handler on each nav item, for
  // two reasons: a module can also be reached by the back button or a deep
  // link, and the nav renders a link per sub-item, so per-click would fire on
  // every page within a module and drown the signal the event exists to give.
  // Only the top-level segment is compared, so moving around inside Payroll is
  // not a switch.
  const { pathname: currentPath } = useLocation()
  const previousModule = useRef<string | null>(null)

  useEffect(() => {
    // "/dashboard/payroll/records" -> "payroll"
    const module = currentPath.split("/").filter(Boolean)[1] ?? null
    if (!module) return

    const previous = previousModule.current
    previousModule.current = module

    // The first module seen in a session is the landing page, not a switch.
    if (previous === null || previous === module) return

    trackEvent("module_tab_switched", { from: previous, to: module })
  }, [currentPath])

  const origin = window.location.origin

  const show =
    origin === stagingUrl || origin === devUrl || origin.includes("localhost")

  const { changeOrganization, user } = useAuthStore()
  const navigate = useNavigate()
  const [canFulfillRequisitions] = usePermissions({
    allowedPermissions: ["can_fulfill_requisitions"],
  })

  const { newPerformanceRoutes } = useNetworkEnabledRoutes()

  const canAccessTandA =
    ["22", "837"].includes(String(user?.userOrganisationId ?? "")) ||
    user?.timeTrackingEnabled === true

  const canAccessOldPerformance =
    ["235"].includes(String(user?.userOrganisationId ?? "")) ||
    isLocalOrDevOrStaging()

  const peopleActions: ActionType[] = [
    {
      title: "People",
      icon: <EmployeeIcon className="w-4" />,
      enabled: true,
      badge: false,
      subActions: [
        {
          title: "Employees",
          route: "employees",
          patterns: [
            { path: "/dashboard/employees/*" },
            { path: "/dashboard/employee/*" },
          ],
          enabled: true,
        },
        {
          title: "Needs Attention",
          route: "needs-attention",
          patterns: [{ path: "/dashboard/needs-attention/*" }],
          enabled: true,
        },
        {
          title: "Departments",
          route: "departments",
          patterns: [{ path: "/dashboard/departments/*" }],
          enabled: true,
        },
        {
          title: "Managers",
          route: "manager",
          patterns: [{ path: "/dashboard/manager/*" }],
          enabled: true,
        },
        {
          title: "Custom groups",
          route: "custom-groups",
          patterns: [{ path: "/dashboard/custom-groups/*" }],
          enabled: true,
        },
        {
          title: "Offers",
          route: "offers",
          patterns: [{ path: "/dashboard/offers/*" }],
          enabled: true,
        },
        {
          title: "Exits",
          route: "exits",
          patterns: [{ path: "/dashboard/exits/*" }],
          enabled: true,
        },
        {
          title: "External admins",
          route: "external-admins",
          patterns: [{ path: "/dashboard/external-admins/*" }],
          enabled: true,
        },
        {
          title: "Confirmation",
          route: "confirmation",
          patterns: [{ path: "/dashboard/confirmation/*" }],
          // enabled: false,
          enabled: true,
        },

        {
          title: "Org. Chart",
          route: "org-chart",
          patterns: [{ path: "/dashboard/org-chart/*" }],
          enabled: true,
        },
        {
          title: "Job Titles",
          route: "job-titles",
          patterns: [{ path: "/dashboard/job-titles/*" }],
          enabled: true,
        },
        {
          title: "Contractors",
          route: "contractor",
          patterns: [{ path: "/dashboard/contractor/*" }],
          enabled: true,
        },
      ],
    },
    {
      title: "Hiring",
      icon: <HiringIcon className="w-4" />,
      enabled: true,
      badge: false,

      patterns: [
        {
          path: "/dashbord/hiring/dashboard",
        },
        {
          path: "/dashbord/hiring/job",
        },
      ],
      // route: `/dashbord/hiring`,
      subActions: [
        {
          title: "Overview",
          route: "hiring/dashboard/",
          patterns: [{ path: "/dashboard/hiring/dashboard/*" }],
          enabled: true,
        },
        {
          title: "Jobs",
          route: `hiring/jobs`,
          patterns: [
            { path: "/dashboard/hiring/jobs/*" },
            { path: "/dashboard/hiring/jobs/*" },
            { path: "/dashboard/hiring/job/*" },

            { path: "/dashboard/hiring/post-job/*" },
          ],
          enabled: true,
        },
        {
          title: "Applicants",
          route: `hiring/applicants`,
          patterns: [{ path: "/dashboard/hiring/applicants/*" }],
          enabled: true,
        },
        {
          title: "Settings",
          route: `hiring/settings`,
          patterns: [{ path: "/dashboard/hiring/settings/*" }],
          enabled: true,
        },
      ],
    },
    {
      title: "Request",
      icon: <RequestIcon className="w-4" />,
      // route: "/dashboard/requests/list",
      patterns: [{ path: "/dashboard/requests/*" }],
      enabled: true,
      badge: false,
      subActions: [
        {
          title: "Requisitions",
          route: "requests/list/requisitions",
          patterns: [
            { path: "/dashboard/requests/list/requisitions/*" },
            { path: "/dashboard/requests/requisitions/setup/*" },
          ],
          enabled: Boolean(canFulfillRequisitions),
          badge: "New",
        },
        {
          title: "Loans",
          route: "requests/list/loans",
          id: "req-loan-nav",
          patterns: [
            { path: "/dashboard/requests/list/loans/*" },
            { path: "/dashboard/requests/loan/create/*" },
            { path: "/dashboard/requests/policies/create/loans/*" },
            { path: "/dashboard/requests/policies/edit/loans/*" },
            { path: "/dashboard/requests/loan/policy/*" },
          ],
          enabled: true,
          // enabled: user?.isHrisEnabled === true ? true : false,
        },
        {
          title: "Salary Advances",
          route: "requests/list/salary-advances",
          patterns: [
            { path: "/dashboard/requests/list/salary-advances/*" },
            { path: "/dashboard/requests/salary-advance/create/*" },
            { path: "/dashboard/requests/policies/create/salary-advance/*" },
            { path: "/dashboard/requests/policies/edit/salary-advance/*" },
            { path: "/dashboard/requests/salary-advance/policy/*" },
          ],
          enabled: true,
          // enabled: user?.isHrisEnabled === true ? true : false,
        },
        {
          title: "Leave",
          route: "requests/list/leaves",
          patterns: [
            { path: "/dashboard/requests/list/leaves/*" },
            { path: "/dashboard/requests/leave/create/*" },
            { path: "/dashboard/requests/policies/create/leaves/*" },
            { path: "/dashboard/requests/policies/edit/leaves/*" },
            { path: "/dashboard/requests/leave/policy/*" },
            { path: "/dashboard/requests/policies/paid-holidays" },
            { path: "/dashboard/requests/policies/federal-holidays" },
          ],
          enabled: true,
          // enabled: user?.isHrisEnabled === true ? true : false,
        },
        {
          title: "Exits",
          route: "requests/list/exits",
          patterns: [
            { path: "/dashboard/requests/list/exits/*" },
            { path: "/dashboard/requests/exit/create/*" },
            { path: "/dashboard/requests/policies/create/exits/*" },
            { path: "/dashboard/requests/policies/edit/exits/*" },
            { path: "/dashboard/requests/exit/bulk-upload" },
            { path: "/dashboard/requests/exit/policy/*" },
          ],
          enabled: true,
        },
        {
          title: "Redeployments",
          route: "requests/list/redeployments",
          patterns: [
            { path: "/dashboard/requests/list/redeployments/*" },
            { path: "/dashboard/requests/redeployment/create/*" },
            { path: "/dashboard/requests/policies/create/redeployments/*" },
            { path: "/dashboard/requests/policies/edit/redeployment/*" },
            { path: "/dashboard/requests/redeployment/bulk-upload" },
            { path: "/dashboard/requests/redeployment/policy/*" },
          ],
          enabled: user?.isHrisEnabled === true ? true : false,
        },
        {
          title: "Documents",
          route: "requests/list/documents",
          patterns: [
            { path: "/dashboard/requests/list/documents/*" },
            { path: "/dashboard/requests/document/create/*" },
            { path: "/dashboard/requests/policies/create/documents/*" },
            { path: "/dashboard/requests/policies/edit/documents/*" },
            { path: "/dashboard/requests/document/policy/*" },
          ],
          enabled: user?.isHrisEnabled === true ? true : false,
        },
        {
          title: "Employee Changes",
          route: "requests/list/employee-changes",
          patterns: [{ path: "/dashboard/requests/list/employee-changes/*" }],
          enabled: true,
          badge: "New",
        },
      ],
    },
    {
      title: "Surveys",
      icon: <SurveyIcon className="w-4" />,
      route: "survey",
      patterns: [{ path: "/dashboard/survey/*" }],
      enabled: true,
      badge: false,

      // enabled: false,
    },

    {
      title: "Documents",
      icon: <DocumentsIcon className="w-4" />,
      enabled: user?.isHrisEnabled === true ? true : false,
      badge: false,
      id: "document-nav",
      subActions: [
        {
          title: "Overview",
          route: "documents",
          patterns: [{ path: "/dashboard/documents/*" }],
          enabled: true,
        },
        {
          title: "Rent Documents",
          route: "rents",
          patterns: [{ path: "/dashboard/rents/*" }],
          enabled: true,
        },
      ],
    },

    // {
    //   title: "Claims",
    //   icon: <CurrencyDollarIcon className="w-5" />,
    //   route: "claims",
    //   patterns: [{ path: "/dashboard/claims/*" }],
    // },
  ]
  const compensationActions: ActionType[] = [
    {
      title: "Payroll",
      icon: <PayrollIcon className="w-4" />,
      // route: "payroll",
      // patterns: [{ path: "/dashboard/payroll/*" }],
      enabled: true,
      badge: false,
      subActions: [
        {
          id: "payroll-overview-nav",
          title: "Overview",
          route: "payroll",
          patterns: [{ path: "/dashboard/payroll" }],
          enabled: true,
        },
        {
          id: "payroll-changes-nav",
          title: "Changes",
          route: "payroll/payrollchange",
          patterns: [
            { path: "/dashboard/payroll/payrollchange/*" },
            {
              path: "/dashboard/payroll/change/bulkupload",
            },
            { path: "/dashboard/payroll/payable-types/*" },
            { path: "/dashboard/payroll/deduction-types/*" },
          ],
          enabled: true,
        },
        {
          title: "Records",
          id: "payroll-records-nav",
          route: "payroll/payrollrecord",
          patterns: [
            { path: "/dashboard/payroll/payrollrecord/*" },
            { path: "/dashboard/payroll/ondemand/create" },
          ],
          enabled: true,
        },
        {
          title: "Skip Employees",
          id: "skip-employees-nav",
          route: "payroll/payrollskip",
          patterns: [{ path: "/dashboard/payroll/payrollskip/*" }],
          enabled: true,
        },
        {
          title: "History",
          id: "payroll-history-nav",
          route: "payroll/history",
          patterns: [{ path: "/dashboard/payroll/history/*" }],
          enabled: true,
        },
        {
          title: "Configuration",
          id: "payroll-config-nav",
          route: "payroll/configuration/pay-schedules",
          patterns: [{ path: "/dashboard/payroll/configuration/*" }],
          enabled: true,
        },
      ],
    },
    {
      title: "Pay Schedules",
      icon: <PayScheduleIcon className="w-4" />,
      enabled: true,
      badge: false,
      subActions: [
        {
          title: "Regular",
          id: "regular-payment-nav",
          route: "payment-schedules/regular",
          patterns: [
            { path: "/dashboard/payment-schedules/regular/*" },
            {
              path: "dashboard/payment-schedules/view-regular-payment",
            },
          ],
          enabled: true,
        },

        {
          title: "Custom",
          id: "custom-payment-nav",
          route: "payment-schedules/custom",
          patterns: [
            { path: "/dashboard/payment-schedules/custom/*" },
            {
              path: "/dashboard/payment-schedules/view-payment",
            },
            {
              path: "/dashboard/payment-schedules/preview-payment",
            },
          ],
          enabled: true,
        },
        {
          title: "Reversals",
          // id: "regular-payment-nav",
          route: "payment-schedules/reversals",
          patterns: [{ path: "/dashboard/payment-schedules/reversals/*" }],
          enabled: true,
        },
      ],
    },
    {
      title: "Wallet",
      icon: <WalletIcon className="w-4" />,
      enabled: true,
      badge: false,
      subActions: [
        {
          title: "Overview",
          route: "wallets/cross-border/overview",
          id: "wallet-nav",
          patterns: [
            {
              path: "/dashboard/wallets/cross-border/overview/*",
            },
            {
              path: "/dashboard/wallets/overview/*",
            },

            {
              path: "/dashboard/wallets/cross-border/transactions/*",
            },
            {
              path: "/dashboard/wallets/cross-border/view/*",
            },
            {
              path: "/dashboard/wallets/cross-border/swap-money/*",
            },
            {
              path: "/dashboard/wallets/cross-border/send-money/*",
            },
            {
              path: "/dashboard/wallets/cross-border/move-funds/*",
            },
          ],
          enabled: user?.userOrganisationId === 108 ? false : true,
        },

        {
          title: "Overview",
          route: "wallets/overview",
          patterns: [{ path: "/dashboard/wallets/overview/*" }],
          enabled: user?.userOrganisationId === 108 ? true : false,
          id: "transaction-nav",
        },
        {
          title: "Transfers",
          route: "wallets/transfers",
          patterns: [{ path: "/dashboard/wallets/transfers/*" }],
          enabled: true,
          id: "transaction-nav",
        },
        {
          title: "Transactions",
          route: "wallets/statements",
          patterns: [{ path: "/dashboard/wallets/statements/*" }],
          enabled: true,
        },
        {
          title: "Integration",
          route: "wallets/integration",
          patterns: [{ path: "/dashboard/wallets/Integration/*" }],
          enabled: true,
          // id: "",
        },
      ],
    },
    {
      title: "Earned Wage Access",
      icon: <EwaIcon className="w-4" />,
      // enabled: user?.isEwaEnabled === true,
      enabled: true,
      badge: false,
      subActions: [
        {
          title: "Analytics",
          route: "ewa/analytics",
          patterns: [{ path: "/dashboard/ewa/analytics/*" }],
          enabled: true,
          id: "ewa-analytics-nav",
        },
        {
          title: "Employees",
          route: "ewa/employees",
          patterns: [{ path: "/dashboard/ewa/employees/*" }],
          enabled: true,
          id: "ewa-employees-nav",
        },
        {
          title: " Withdrawals ",
          route: "ewa/withdrawals",
          patterns: [{ path: "/dashboard/ewa/withdrawals/*" }],
          enabled: true,
          id: "ewa-withdrawals-nav",
        },

        {
          title: "Payment",
          route: "ewa/invoice",
          patterns: [{ path: "/dashboard/ewa/invoice/*" }],
          enabled: true,
          id: "ewa-invoice-nav",
        },
        {
          title: "Policy Management",
          route: "ewa/policy-management",
          patterns: [{ path: "/dashboard/ewa/policy-management/*" }],
          enabled: true,
        },
      ],
    },

    {
      title: "Benefits",
      icon: <GiftIcon className="w-4" />,
      route: "benefits",
      badge: false,
      patterns: [{ path: "/dashboard/benefits/*" }],
      enabled: true,
      id: "benefits-nav",
    },
  ]
  const taskActions: ActionType[] = [
    ...newPerformanceRoutes,
    {
      title: "Performance",
      icon: <PerformanceIcon className="w-4" />,
      enabled: user?.isPerformanceEnabled === true && canAccessOldPerformance,
      badge: "Legacy" as const,
      subActions: [
        {
          title: "Period",
          id: "pf-period-nav",
          route: "performance/performance-period",
          patterns: [{ path: "/dashboard/performance/performance-period/*" }],
          enabled: true,
        },
        {
          title: "Objectives",
          id: "pf-objectives-nav",
          route: "performance/performance-objectives",
          patterns: [
            { path: "/dashboard/performance/performance-objectives/*" },
          ],
          enabled: true,
        },
        {
          title: "Groups",
          id: "pf-group-nav",
          route: "performance/groups",
          patterns: [{ path: "/dashboard/performance/groups/*" }],
          enabled: true,
        },
        {
          title: "Reviews",
          id: "pf-reviews-nav",
          route: "performance/reviews",
          patterns: [{ path: "/dashboard/performance/reviews/*" }],
          enabled: true,
        },
        {
          title: "Ratings",
          id: "pf-ratings-nav",
          route: "performance/ratings",
          patterns: [{ path: "/dashboard/performance/ratings/*" }],
          enabled: true,
        },

        {
          title: "Conversations",
          route: "performance/conversations",
          patterns: [
            { path: "/dashboard/performance/conversations/*" },
            { path: "/dashboard/performance/add-prompt/*" },
          ],
          enabled: true,
        },
        {
          title: "Feedback",
          route: "performance/feedback",
          patterns: [{ path: "/dashboard/performance/feedback/*" }],
          enabled: true,
        },
        // {
        //   title: "Reviews",
        //   route: "reviews",
        //   patterns: [{ path: "/dashboard/reviews/*" }],
        // },
      ],
    },

    {
      title: "Time & Attendance",
      icon: <TimeAndAttendanceIcon className="w-4" />,
      route: "time-and-attendance",
      subActions: [
        {
          enabled: true,
          route: "time-and-attendance/overview",
          title: "Overview",
          patterns: [{ path: "/dashboard/time-and-attendance/overview/*" }],
        },
      ],
      patterns: [{ path: "/dashboard/time-and-attendance/*" }],
      enabled: canAccessTandA,
      badge: "New" as const,
    },
    {
      title: "Time Tracking",
      icon: <ClockIcon className="w-4" />,
      route: "time-tracking",
      subActions: [
        {
          enabled: true,
          route: "time-tracking/absence",
          title: "Absence",
          patterns: [{ path: "/dashboard/time-tracking/absence/*" }],
        },
      ],
      patterns: [{ path: "/dashboard/time-tracking/*" }],
      enabled: true,
      badge: "Legacy" as const,
    },
    {
      title: "Tasks",
      icon: <TaskIcon className="w-4" />,
      route: "tasks",
      patterns: [{ path: "/dashboard/tasks/*" }],
      enabled: true,
      badge: false,
      id: "tasks-nav",
    },
    {
      title: "Disciplinary",
      icon: <DisciplinaryIcon className="w-4" />,
      route: "disciplinary",
      patterns: [{ path: "/dashboard/disciplinary" }],
      enabled: user?.isHrisEnabled === true,
      badge: false,
      subActions: [
        {
          id: "suspensions-nav",
          title: "Suspensions",
          route: "disciplinary/suspensions",
          patterns: [{ path: "/dashboard/disciplinary/suspensions/*" }],
          enabled: true,
        },
        {
          id: "violations-nav",
          title: "Violations",
          route: "disciplinary/violations",
          patterns: [{ path: "/dashboard/disciplinary/violations/*" }],
          enabled: true,
        },
        {
          id: "policies-nav",
          title: "Policies",
          route: "disciplinary/policies",
          patterns: [{ path: "/dashboard/disciplinary/policies/*" }],
          enabled: true,
        },
        {
          title: "Triggers",
          id: "triggers-nav",
          route: "disciplinary/triggers",
          patterns: [{ path: "/dashboard/disciplinary/triggers/*" }],
          enabled: true,
        },
        {
          title: "Violation Types",
          id: "violation-types-nav",
          route: "disciplinary/violation-types",
          patterns: [{ path: "/dashboard/disciplinary/violation-types/*" }],
          enabled: true,
        },
      ],
    },
  ]
  const otherActions: ActionType[] = [
    // {
    //   title: "Reports",
    //   icon: <DocumentChartBarIcon className="w-5" />,
    //   route: "reports",
    //   patterns: [{ path: "/dashboard/reports/*" }],
    // },
    {
      title: "Settings",
      icon: <SettingsIcon className="w-4" />,
      enabled: true,
      badge: false,
      subActions: [
        // {
        //   title: "Permissions",
        //   route: "settings/permissions",
        //   patterns: [{ path: "/dashboard/settings/permissions/*" }],
        //   enabled: true,
        // },
        {
          title: "Admins",
          route: "settings/permissions/admins",
          patterns: [{ path: "/dashboard/settings/permissions/*" }],
          enabled: true,
        },
        // {
        //   title: "Managers",
        //   route: "settings/permissions/manager",
        //   patterns: [{ path: "/dashboard/settings/permissions/*" }],
        //   enabled: true,
        // },

        {
          title: "HR & Payroll",
          route: "settings/hr-and-payroll",
          patterns: [{ path: "/dashboard/settings/hr-and-payroll/*" }],
          enabled: true,
        },
        {
          title: "Plan & Billing",
          route: "settings/plan-and-billing",
          patterns: [{ path: "/dashboard/settings/plan-and-billing/*" }],
          enabled: true,
        },
        {
          title: "Customization",
          route: "settings/customization",
          patterns: [{ path: "/dashboard/settings/customization/*" }],
          enabled: true,
        },

        {
          title: "Approval Workflows",
          route: "settings/approval-workflow",
          patterns: [{ path: "/dashboard/settings/approval-workflow/*" }],
          enabled: true,
        },
        {
          title: "Integrations",
          route: "settings/integrations",
          patterns: [{ path: "/dashboard/settings/integrations/*" }],
          enabled:
            user?.userOrganisationId == 354 || // oando
            user?.userOrganisationId == 469 || // hugotech
            show,
        },
        {
          title: "Single Sign-On",
          route: "settings/sso",
          patterns: [{ path: "/dashboard/settings/sso/*" }],
          enabled: true,
        },
        {
          title: "Roles & Permissions",
          route: "settings/roles-permissions",
          patterns: [{ path: "/dashboard/settings/roles-permissions/*" }],
          enabled: true,
        },
      ],
    },
    {
      title: "Reports",
      icon: <ReportsIcon className="w-4" />,
      enabled: true,
      badge: false,
      patterns: [
        { path: "/dashboard/report/*" },
        { path: "/dashboard/report/custom-report/*" },
        {
          path: "/dashboard/report/employee-report-builder",
        },
        {
          path: "/dashboard/report-preview/consolidated-payroll-journal",
        },
      ],
      subActions: [
        {
          title: "Payroll Reports",
          route: "report/payroll",
          patterns: [
            { path: "/dashboard/report/payroll" },
            {
              path: "/dashboard/report/payroll-journal",
            },
            {
              path: "/dashboard/report-preview/payroll-journal",
            },
            {
              path: "/dashboard/report-preview/consolidated-payroll-journal",
            },
            {
              path: "/dashboard/report/payroll-summary",
            },

            {
              path: "/dashboard/report-preview/payroll-summary",
            },
            {
              path: "/dashboard/report/payroll-variance",
            },
            {
              path: "/dashboard/report-preview/payroll-variance",
            },
            {
              path: "/dashboard/report/year-to-date",
            },
            {
              path: "/dashboard/report-preview/year-to-date",
            },
            {
              path: "/dashboard/report/statutory-benefits",
            },
            {
              path: "/dashboard/report-preview/statutory-benefits",
            },
            {
              path: "/dashboard/report/payroll-change",
            },
            {
              path: "/dashboard/report-preview/payroll-change",
            },
            {
              path: "/dashboard/report/payroll-exemption",
            },
            {
              path: "/dashboard/report-preview/payroll-exemption",
            },
            {
              path: "/dashboard/report/paygrade-summary",
            },
            {
              path: "/dashboard/report-preview/paygrade-summary",
            },
            {
              path: "/dashboard/report/custom-benefits",
            },
            {
              path: "/dashboard/report-preview/custom-benefits",
            },
            {
              path: "/dashboard/report/statutory-payments",
            },
            {
              path: "/dashboard/report-preview/statutory-payments",
            },
          ],
          enabled: true,
          id: "reports-summary-nav",
        },

        {
          title: "Employee Reports",
          route: "report/employees",
          patterns: [
            { path: "/dashboard/report/employees/*" },
            {
              path: "/dashboard/report/employee-request",
            },
            {
              path: "/dashboard/report/leave-balances",
            },
            {
              path: "/dashboard/report-preview/custom-fields",
            },
            {
              path: "/dashboard/report-preview/employee-summary",
            },
            {
              path: "/dashboard/report-preview/employee_request",
            },
            {
              path: "/dashboard/report-preview/leave-balances",
            },
          ],
          enabled: true,
          id: "employee-reports-nav",
        },
        {
          title: "Custom Reports",
          route: "report/custom",
          patterns: [
            { path: "/dashboard/report/custom-report" },
            { path: "/dashboard/report/custom" },
            {
              path: "/dashboard/report/employee-report-builder",
            },
            {
              path: "/dashboard/report-preview/builder/*",
            },
          ],
          enabled: true,
          id: "custom-reports-nav",
        },
      ],
    },
    {
      title: "Assets",
      icon: <AssetsIcon className="w-4" />,
      enabled: user?.isHrisEnabled === true ? true : false,
      badge: false,
      subActions: [
        {
          title: "Overview",
          route: "assets",
          patterns: [{ path: "/dashboard/assets/*" }],
          enabled: true,
        },
        {
          title: "Assignments",
          route: "assignments",
          patterns: [{ path: "/dashboard/assignments/*" }],
          enabled: true,
        },
      ],
    },
    {
      title: "Audit Log",
      icon: <AuditLogIcon className="w-4" />,
      enabled: true,
      route: "audits",
      patterns: [{ path: "/dashboard/audits/*" }],
      badge: false,
    },
    {
      title: "Announcements",
      route: "settings/announcements",
      patterns: [
        { path: "/dashboard/settings/announcements/*" },
        { path: "/dashboard/settings/announcement_groups/*" },
      ],
      enabled: true,
      badge: false,
      icon: <AnnouncementsIcon className="w-4" />,
      subActions: [
        {
          title: "Overview",
          route: "settings/announcements",
          patterns: [{ path: "/dashboard/settings/announcements/*" }],
          enabled: true,
        },
        {
          title: "Groups",
          route: "settings/announcement_groups",
          patterns: [{ path: "/dashboard/settings/announcement_groups/*" }],
          enabled: true,
        },
      ],
    },
  ]
  const extraActions: ActionType[] = [
    {
      title: "Calendars",
      icon: <CalendarsIcon className="w-4" />,
      route: "calendar",
      patterns: [{ path: "/dashboard/calendar/*" }],
      enabled: true,
      badge: false,
    },
    {
      title: "Help",
      icon: <QuestionMarkCircleIcon className="w-4" />,
      route: "https://support.paidhr.com",
      patterns: [{ path: "/dashboard/help/*" }],
      enabled: isPadeDomain(),
      badge: false,
      newTab: true,
    },
    /* {
      title: "Support",
      icon: <LifebuoyIcon className="w-4" />,
      route: "https://support.padehcm.com/help",
      patterns: [{ path: "/dashboard/support/*" }],
      enabled: true,
      badge: false,
    },*/
  ]
  const { toast } = useAppNotificationStore()

  /**
   * Stays true from the click until the page is replaced.
   *
   * The mutation's own loading flag drops the moment the request answers, which is before the
   * browser has navigated -- so the overlay came down and the page being left flashed back
   * into view between the two halves of the switch.
   */
  const [switching, setSwitching] = useState(false)

  const { isLoading: isLoading, mutate: submit } = useMutation({
    mutationFn: async (id: string) => {
      const res = await newArchAxiosInstance.post<IBaseRes<IUser>>(
        `/admin/company/switch?id=${id}`
      )
      return res.data
    },
    onSuccess: async (data) => {
      if (data.success) {
        const newToken = data.data.accessToken
        const newUser = data.data as IUser
        // data.data leaves the following fields out, so we need to add them manually from the previous user object
        if (user) {
          newUser.userId = user.userId
          newUser.userAvatarFileUrl = user.userAvatarFileUrl
          newUser.userFirstName = user.userFirstName
          newUser.userFullName = user.userFullName
          newUser.userKey = user.userKey
          newUser.userLastName = user.userLastName
          newUser.userName = user.userName
          newUser.members = user.members
          newUser.userStatusId = user.userStatusId
          newUser.isUserOrganisation = user.isUserOrganisation
          newUser.userRoles = user.userRoles
          newUser.canSwitch = user.canSwitch
          newUser.userPhone = user.userPhone
          newUser.userPermissions = user.userPermissions
          newUser.personKey = user.personKey
          newUser.lastLogin = user.lastLogin
          newUser.isSecondaryAdmin = user.isSecondaryAdmin
          newUser.isPrimaryAdmin = user.isPrimaryAdmin
          newUser.isAdmin = user.isAdmin
          newUser.controlOrganisationId = user.controlOrganisationId
          newUser.adminsCanSwitchOrganisations =
            user?.adminsCanSwitchOrganisations
          newUser.isActive = user?.isActive
          newUser.userOrganisationAlias = user?.userOrganisationAlias
          newUser.hasPendingInvoice = user?.hasPendingInvoice
          newUser.isConfirmationEnabled = user?.isConfirmationEnabled
          newUser.splitPaymentEnabled = user?.splitPaymentEnabled
          newUser.userWalletId = user?.userWalletId
          newUser.unreadNotificationCount = user?.unreadNotificationCount
          newUser.unreadNotifications = user?.unreadNotifications
          newUser.isWalletSetup = user?.isWalletSetup
          newUser.isManager = user?.isManager
          newUser.allowCrossReporting = user?.allowCrossReporting
          newUser.isStatutoryInfoSetup = user?.isStatutoryInfoSetup
          newUser.isWebhookIntegrationEnabled =
            user?.isWebhookIntegrationEnabled
        }
        await changeOrganization(newToken, newUser).then(() => {
          // The switch has already shown the app shell in grey. Marking the reload keeps
          // the boot from drawing a second loading screen on top of it.
          markAccountSwitch()
          window.location.href = "/dashboard"
        })
      } else {
        // Nothing is going to replace the page, so the overlay must come down or the person
        // is left staring at a shell that will never resolve.
        setSwitching(false)
      }
    },
    onError: () => setSwitching(false),
  })
  const handleChangeOrg = (orgId: any) => {
    setSwitching(true)
    submit(orgId)
  }

  // Focus the search box when the list opens and clear it once it closes, so a
  // stale query never hides accounts the next time the list is opened.
  useEffect(() => {
    if (isBusinessListOpen) {
      businessSearchRef.current?.focus()
    } else {
      setBusinessSearch("")
    }
  }, [isBusinessListOpen])

  const otherBusinesses =
    user?.members?.filter((item) => item?.name !== user?.userOrganisation) ?? []
  const businessQuery = businessSearch.trim().toLowerCase()
  // Match on the account name or any of its identifiers (id, company id, key)
  // so an admin can jump to an account without scrolling the whole list.
  const filteredBusinesses = businessQuery
    ? otherBusinesses.filter((org) =>
        [org?.name, org?.id, org?.company_id, org?.key].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(businessQuery)
        )
      )
    : otherBusinesses

  const handleToggleSidebar = () => {
    toggleSidebar(false)
    document.body.classList.remove("overflow-hidden")
  }

  return (
    <>
      {isSidebarOpen && (
        <div
          className="md:hidden fixed inset-0 z-20 bg-black/30  cursor-pointer backdrop-blur-sm"
          onClick={handleToggleSidebar}
        />
      )}
      <SideNavFrame
        isSidebarOpen={isSidebarOpen}
        compact={compact}
        zIndexClass="z-20"
        backdrop
      >
        <div
          className={`w-full px-3 pb-[18px] flex flex-col justify-start gap-3 relative z-[2] transition-[padding] duration-300 ease-in-out motion-reduce:transition-none ${
            compact ? "pt-3" : "pt-[18px]"
          }`}
        >
          <div className="relative font-avenir">
            {/* Org card. The two faces sit in grid rows that trade 1fr/0fr so
                the height glides between the full card and the 40px avatar
                instead of jumping (same trick as .sidebar-submenu). */}
            <div
              className={`bg-primary600 rounded-md shadow cursor-pointer grid overflow-hidden transition-[grid-template-rows,padding] duration-300 ease-in-out motion-reduce:transition-none ${
                compact ? "p-0" : "p-3"
              }`}
              style={{ gridTemplateRows: compact ? "0fr 1fr" : "1fr 0fr" }}
              onClick={() => {
                if (compact) {
                  setCollapsed(false)
                  return
                }
                if (user?.canSwitch !== true) {
                  return
                }
                toggleBusinessListOpen(!isBusinessListOpen)
              }}
            >
              <div
                aria-hidden={compact}
                className={`min-h-0 overflow-hidden ${fadeOutWhenCompact(
                  compact
                )}`}
              >
                <div className="flex flex-col items-start justify-between">
                  <div className="mb-2">
                    {isPadeDomain() === true ? (
                      <img
                        src={PadeLogoWhite}
                        alt=""
                        className="w-[100px]"
                      />
                    ) : (
                      <img
                        src={customizationSettings?.brand?.menuLogoUrl}
                        alt=""
                        className="w-[100px]"
                      />
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-2 w-full">
                    <p className="text-a11y/90  text-xs">
                      {user?.userOrganisation}
                    </p>
                    {user?.canSwitch === true && (
                      <div className="flex justify-center  items-center">
                        <ChevronDownIcon className="w-5 h-5 text-a11y/90 " />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div
                aria-hidden={!compact}
                className={`min-h-0 overflow-hidden ${fadeInWhenCompact(
                  compact
                )}`}
              >
                <Tooltip
                  placement="right"
                  title={compact ? user?.userOrganisation : ""}
                  mouseEnterDelay={0.15}
                >
                  <div>
                    <BrandMark name={user?.userOrganisation} />
                  </div>
                </Tooltip>
              </div>
            </div>
            {user?.canSwitch === true && isBusinessListOpen && !compact && (
              <div
                className={`mt-1 bg-primary600 pb-2 rounded-md shadow absolute w-full z-[1] overflow-hidden duration-150 ${
                  isBusinessListOpen ? "max-h-[280px] overflow-y-auto" : "h-0"
                }`}
              >
                <div className="sticky top-0 bg-primary600 px-3 pt-3 pb-2">
                  <div className="flex items-center gap-2 rounded-md bg-primary500 px-2 py-1.5">
                    <MagnifyingGlassIcon className="w-4 h-4 shrink-0 text-a11y/70" />
                    <input
                      ref={businessSearchRef}
                      type="search"
                      value={businessSearch}
                      onChange={(e) => setBusinessSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Escape") {
                          toggleBusinessListOpen(false)
                        }
                      }}
                      placeholder="Search accounts"
                      aria-label="Search sub accounts"
                      className="w-full bg-transparent text-sm text-a11y placeholder:text-a11y/60 outline-none"
                    />
                  </div>
                </div>
                <div className="flex flex-col items-start pb-3 pt-1 px-5 space-y-4">
                  {businessQuery && filteredBusinesses.length === 0 && (
                    <p className="text-a11y/70 text-sm">
                      No accounts match &ldquo;{businessSearch.trim()}&rdquo;
                    </p>
                  )}
                  {filteredBusinesses.map((org) => (
                    <button
                      key={org?.key ?? org?.id}
                      className="text-a11y/90  text-left text-sm w-full"
                      onClick={() => {
                        if (
                          user?.adminsCanSwitchOrganisations === true ||
                          user?.isPrimaryAdmin === true ||
                          user?.isSecondaryAdmin === true
                        ) {
                          handleChangeOrg(`${org?.key}`)
                          toggleSidebar(false)
                        } else {
                          toast.unauthorized(
                            "You are not authorized to switch between accounts"
                          )
                        }
                      }}
                    >
                      {org.name}
                    </button>
                  ))}

                  <button
                    className="text-a11y/90 px-3 py-1 flex items-start justify-start rounded-full text-sm border border-a11y/90"
                    onClick={() => {
                      navigate("/dashboard/company-details/new")
                      handleToggleSidebar()
                    }}
                  >
                    <span className="mr-2">
                      <PlusIcon className="w-4 h-4 text-a11y" />
                    </span>
                    <span>Add new Business</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          {/* Menu */}
          <div className="h-full w-full flex flex-col gap-9">
            {/* Dashboard */}
            <div className="gap-3 flex flex-col">
              {user?.setupStatus === "Approved" ? (
                ""
              ) : (
                <NavTab
                  action={{
                    title: "Onboarding",
                    route: "company-onboarding",
                    icon: <Squares2X2Icon className="w-4" />,
                    patterns: [{ path: "/dashboard/company-onboarding" }],
                    enabled: true,
                    badge: false,
                  }}
                  toggleSidebar={handleToggleSidebar}
                  compact={compact}
                />
              )}

              <NavTab
                id="dashboard-nav"
                action={{
                  title: "Dashboard",
                  route: "",
                  icon: <DashboardIcon className="w-4" />,
                  patterns: [{ path: "/dashboard" }],
                  enabled: true,
                  badge: false,
                }}
                toggleSidebar={handleToggleSidebar}
                compact={compact}
              />
              <NavTab
                id="cd-nav"
                action={{
                  title: "Company Details",
                  icon: <CompanyIcon className="w-4" />,
                  route: "company-details",
                  patterns: [{ path: "/dashboard/company-details/*" }],
                  enabled: true,
                  badge: false,
                }}
                toggleSidebar={handleToggleSidebar}
                compact={compact}
              />
            </div>
            {/* Other Menu */}
            <div className=" w-full flex flex-col gap-8">
              {/* People */}
              <div className=" w-full flex flex-col gap-2">
                <SectionHeading
                  title="PEOPLE"
                  compact={compact}
                />
                <div className=" w-full flex flex-col gap-0.5 3">
                  {peopleActions.map((action, index) =>
                    !action.enabled ? null : (
                      <NavTab
                        id={action.id}
                        action={action}
                        key={index}
                        index={index}
                        toggleSidebar={handleToggleSidebar}
                        compact={compact}
                      />
                    )
                  )}
                </div>
              </div>
              {/* Payroll */}
              {!user?.isPayrollEnabled ? null : (
                <div className=" w-full flex flex-col gap-2">
                  <SectionHeading
                    title="COMPENSATION"
                    compact={compact}
                  />
                  <div className="w-full flex flex-col gap-0.5 3">
                    {compensationActions.map((action, index) =>
                      !action.enabled ? (
                        ""
                      ) : (
                        <NavTab
                          action={action}
                          key={index}
                          index={index}
                          toggleSidebar={handleToggleSidebar}
                          compact={compact}
                          id={action.id}
                        />
                      )
                    )}
                  </div>
                </div>
              )}
              {/* Tasks */}

              <div className="w-full flex flex-col gap-2">
                <SectionHeading
                  title="PRODUCTIVITY"
                  compact={compact}
                />
                <div className=" w-full flex flex-col gap-0.5 3">
                  {taskActions.map((action, index) =>
                    !action.enabled ? (
                      ""
                    ) : (
                      <NavTab
                        action={action}
                        key={index}
                        index={index}
                        toggleSidebar={handleToggleSidebar}
                        compact={compact}
                        id={action.id}
                      />
                    )
                  )}
                </div>
              </div>

              {/* Documents */}
              <div className="w-full flex flex-col gap-2">
                <SectionHeading
                  title="OTHERS"
                  compact={compact}
                />
                <div className=" w-full flex flex-col gap-0.5 3">
                  {otherActions.map((action, index) =>
                    !action.enabled ? null : (
                      <NavTab
                        action={action}
                        key={index}
                        index={index}
                        toggleSidebar={handleToggleSidebar}
                        compact={compact}
                      />
                    )
                  )}
                </div>
              </div>
              {/* Extras */}
              <div className="w-full flex flex-col gap-2 pb-40">
                <SectionHeading
                  title="EXTRAS"
                  compact={compact}
                />
                <div className=" w-full flex flex-col gap-0.5 3">
                  {extraActions.map((action, index) =>
                    !action.enabled ? null : (
                      <NavTab
                        action={action}
                        key={index}
                        index={index}
                        toggleSidebar={handleToggleSidebar}
                        compact={compact}
                      />
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SideNavFrame>

      {(isLoading || switching) && (
        // Switching account rebuilds the whole app for the new organisation,
        // so this shows the app being rebuilt rather than a spinner veiling
        // the old one - the same shell the cold load draws, so the two
        // entrances into the app look alike. Opaque, because what is behind it
        // belongs to the account being left.
        <div className="fixed inset-0 z-50 h-screen w-screen">
          <AppBootSkeleton />
        </div>
      )}
    </>
  )
}

const useNetworkEnabledRoutes = () => {
  const user = useAuthStore((state) => state.user)
  const { isLoadingPerformanceSetupProgress, isPerformanceSetupComplete } =
    useAuthStore((state) => ({
      isLoadingPerformanceSetupProgress:
        state.user?.isLoadingPerformanceTracker,
      isPerformanceSetupComplete: state.user?.isNewPerformanceSetupCompleted,
    }))

  const newPerformanceRoutes = isLoadingPerformanceSetupProgress
    ? []
    : [
        {
          title: "Performance",
          icon: <PerformanceIcon className="w-4" />,
          enabled: user?.isPerformanceEnabled === true,
          // enabled: isLocalOrDevOrStaging() || user?.userOrganisationId === 22,
          badge: false as ActionType["badge"],
          subActions: isPerformanceSetupComplete
            ? [
                {
                  title: "Overview",
                  route: "performance/overview",
                  patterns: [{ path: "/dashboard/performance/overview/*" }],
                  enabled: true,
                },
                {
                  title: "OKRs / KPIs",
                  route: "performance/okr",
                  patterns: [
                    { path: "/dashboard/performance/okr/*" },
                    { path: "/dashboard/performance/okr/review/*" },
                  ],
                  enabled: true,
                },
                // {
                //   title: "Check-Ins",
                //   route: "#",
                //   patterns: [],
                //   enabled: true,
                //   badge: "Coming Soon" as const,
                // },
                // {
                //   title: "Feedback",
                //   route: "#",
                //   patterns: [],
                //   enabled: true,
                //   badge: "Coming Soon" as const,
                // },
                {
                  title: "Review",
                  route: "performance/review/review-period",
                  patterns: [{ path: "/dashboard/performance/review/*" }],
                  enabled: true,
                },
                {
                  title: "Settings",
                  route: "performance/settings",
                  patterns: [{ path: "/dashboard/performance/settings/*" }],
                  enabled: true,
                },
              ]
            : [
                {
                  title: "Setup",
                  route: "performance/setup",
                  patterns: [{ path: "/dashboard/performance/setup/*" }],
                  enabled: true,
                },
              ],
        },
      ]
  return { newPerformanceRoutes }
}
